import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { dashboardApi } from '@/utils/api';
import { useCaptureModal } from '@/contexts/CaptureModalContext';
import { Plus, ArrowRight, Users, Phone, Mail, FileText, PenLine, CheckSquare2, ListChecks } from 'lucide-react';
import { useTaskDrawer } from '@/contexts/TaskDrawerContext';
import { toast } from 'sonner';
import { relTime } from '@/utils/time';
import { RYG_META } from '@/utils/ryg';
import WorkspaceTiles from '@/components/WorkspaceTiles';

function initials(name) {
  return name?.trim().split(/\s+/).map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
}
const AV_COLORS = ['blue', 'green', 'butter', 'clay'];
const avColor = (name) => AV_COLORS[(name || '').length % AV_COLORS.length];

const TYPE_META = {
  meeting: { cls: 'meeting', Icon: Users },
  audio: { cls: 'call', Icon: Phone },
  follow_up: { cls: 'email', Icon: Mail },
  text: { cls: 'email', Icon: FileText },
  quick_note: { cls: 'voicemail', Icon: PenLine },
  manual: { cls: 'voicemail', Icon: PenLine },
};
const typeMetaOf = (src) => TYPE_META[src] || TYPE_META.meeting;

function priPill(task) {
  if (task.priority === 'high') return { cls: 'hi', label: 'High' };
  if (task.priority === 'low') return { cls: 'lo', label: 'Low' };
  return { cls: 'md', label: 'Med' };
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { openModal } = useCaptureModal();
  const { openTaskDrawer } = useTaskDrawer();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await dashboardApi.get();
      setData(res.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 401 && status !== 403) toast.error('Failed to load dashboard');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCard = (id) => navigate(`/action-cards/${id}`);
  const firstName = user?.name?.split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const overdue = data?.overdue_tasks || [];
  const today = data?.today_tasks || [];
  const overdueIds = new Set(overdue.map(t => t.id));
  const tasks = Array.from(new Map([...overdue, ...today].map(t => [t.id, t])).values());
  const cards = data?.recent_action_cards || [];
  const s = data?.stats || {};
  const notStarted = s.tasks_not_started || 0;
  const inProgress = s.tasks_in_progress || 0;
  const completed = s.tasks_completed || 0;
  const totalStat = notStarted + inProgress + completed;
  const pct = (v) => totalStat ? Math.round((v / totalStat) * 100) : 0;
  const completionPct = pct(completed);

  const subline = overdue.length
    ? `${overdue.length} task${overdue.length !== 1 ? 's' : ''} overdue — let's clear them.`
    : tasks.length
    ? `${tasks.length} task${tasks.length !== 1 ? 's' : ''} need${tasks.length === 1 ? 's' : ''} you today.`
    : "You're all caught up. Nothing slipping today.";

  const statusRows = [
    { label: 'Not started', value: notStarted, color: 'var(--faint)', status: 'not_started' },
    { label: 'In progress', value: inProgress, color: 'var(--blue)', status: 'in_progress' },
    { label: 'Completed', value: completed, color: 'var(--green)', status: 'done' },
  ];

  // Account-wide RYG health: red = overdue open · yellow = open on-track · green = done.
  const redCount = s.tasks_overdue || 0;
  const greenCount = completed;
  const yellowCount = Math.max(0, notStarted + inProgress - redCount);
  const rygTotal = redCount + yellowCount + greenCount;
  const rygSegs = [
    { key: 'red', value: redCount },
    { key: 'yellow', value: yellowCount },
    { key: 'green', value: greenCount },
  ];

  return (
    <div className="pagewrap home-v2" data-testid="dashboard-page">
      <div className="page-head">
        <div>
          <h1>{greeting}, {firstName}</h1>
          <p className="sub">{loading ? 'Loading your day…' : subline}</p>
        </div>
        <button className="btn-capture-top" onClick={openModal} data-testid="dashboard-new-action-button">
          <Plus size={16} />Capture
        </button>
      </div>

      {/* Account-wide RYG health strip */}
      {!loading && rygTotal > 0 && (
        <div className="home-health" data-testid="dashboard-ryg-strip">
          <div className="hh-bar" title={`${redCount} needs attention · ${yellowCount} in progress · ${greenCount} done`}>
            {rygSegs.filter(x => x.value > 0).map(x => (
              <span key={x.key} className="hh-seg" data-testid={`dashboard-ryg-seg-${x.key}`}
                style={{ flex: x.value, background: RYG_META[x.key].color }} />
            ))}
          </div>
          <div className="hh-chips">
            {rygSegs.map(x => (
              <button key={x.key} className="ryg-chip" data-testid={`dashboard-ryg-${x.key}`}
                onClick={() => navigate(`/tasks?ryg=${x.key}`)}
                title={`View ${RYG_META[x.key].label.toLowerCase()} tasks`}>
                <span className="ryg-dot" style={{ background: RYG_META[x.key].color }} />
                {RYG_META[x.key].label}<b>{x.value}</b>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="layout">
        <div className="col-main">
          {/* Needs you today */}
          <div>
            <div className="sec-row">
              <div className="sh"><h3>Needs you today</h3><span className="n">({tasks.length})</span></div>
              <button className="link" onClick={() => navigate('/tasks')} data-testid="dashboard-all-tasks-link">
                View all tasks<ArrowRight />
              </button>
            </div>
            <div className="list-card" data-testid="dashboard-today-tasks-list">
              {loading ? (
                <div style={{ padding: '24px 18px', color: 'var(--mute)', fontSize: 13.5 }}>Loading tasks…</div>
              ) : tasks.length ? (
                tasks.slice(0, 6).map(t => {
                  const p = priPill(t);
                  const isOverdue = overdueIds.has(t.id);
                  const src = t.workspace_name || t.action_card_title;
                  return (
                    <div key={t.id} data-testid={`dashboard-task-container-${t.id}`}>
                      <div className="row" data-testid="dashboard-task-row"
                        onClick={() => openTaskDrawer(t.id, { onChanged: load })}>
                        <div className="r-name">
                          <span className="tt">{t.title}</span>
                          {src && <span className="src-chip"><FileText />
                            <span>{src}{t.assignee_name ? ` · ${t.assignee_name}` : ''}</span></span>}
                        </div>
                        <div className="r-asg">
                          {t.assignee_name
                            ? <div className={`av ${avColor(t.assignee_name)}`} data-tip={t.assignee_name}>{initials(t.assignee_name)}</div>
                            : <div className="av ghost" data-tip="Unassigned">?</div>}
                        </div>
                        <div className="r-pri"><span className={`pill ${p.cls}`}>{p.label}</span></div>
                        <div className={`r-due${isOverdue ? ' over' : ' today'}`}>{isOverdue ? 'Overdue' : 'Today'}</div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="home-empty">
                  <div className="eic"><CheckSquare2 size={20} /></div>
                  <div className="et">No tasks due today</div>
                  <div className="es">You're all caught up — nice work.</div>
                </div>
              )}
            </div>
          </div>

          {/* Recent Action Cards */}
          <div>
            <div className="sec-row">
              <div className="sh"><h3>Recent Action Cards</h3><span className="n">({cards.length})</span></div>
              <button className="link" onClick={() => navigate('/action-cards')} data-testid="dashboard-all-cards-link">
                View all<ArrowRight />
              </button>
            </div>
            <div className="list-card" data-testid="dashboard-recent-action-cards">
              {loading ? (
                <div style={{ padding: '24px 18px', color: 'var(--mute)', fontSize: 13.5 }}>Loading cards…</div>
              ) : cards.length ? (
                cards.slice(0, 5).map(c => {
                  const meta = typeMetaOf(c.source);
                  const Icon = meta.Icon;
                  const chip = c.workspace_name || c.tags?.[0];
                  return (
                    <div key={c.id} className="row" data-testid="dashboard-action-card-tile" onClick={() => openCard(c.id)}>
                      <div className="r-name">
                        <div className={`type-ic ${meta.cls}`}><Icon size={13} /></div>
                        <span className="tt">{c.title || c.contact_name || 'Untitled'}</span>
                        {chip && <span className="src-chip"><Users /><span>{chip}</span></span>}
                      </div>
                      <div className="r-tk">{c.task_count != null ? <b>{c.task_count}</b> : <b>0</b>}</div>
                      <div className="r-when">{relTime(c.created_at)}</div>
                    </div>
                  );
                })
              ) : (
                <div className="home-empty">
                  <div className="eic"><ListChecks size={20} /></div>
                  <div className="et">No Action Cards yet</div>
                  <div className="es">Capture a conversation to get started.</div>
                </div>
              )}
            </div>
          </div>

          <WorkspaceTiles />
        </div>

        {/* Rail */}
        <aside className="col-rail">
          <div className="rail-card" data-testid="dashboard-stats">
            <h3><CheckSquare2 />Task status</h3>
            {statusRows.map(r => (
              <div className="st-row" key={r.label} role="button" tabIndex={0}
                onClick={() => navigate(`/tasks?status=${r.status}`)}
                data-testid={`dashboard-stat-${r.status}`}
                title={`View ${r.label.toLowerCase()} tasks`}>
                <div className="top">
                  <span className="k"><span className="swatch" style={{ background: r.color }} />{r.label}</span>
                  <span className="v">{r.value}</span>
                </div>
                <div className="bar"><i style={{ width: `${Math.max(4, pct(r.value))}%`, background: r.color }} /></div>
              </div>
            ))}
            <div className="foot-line"><span>Completion this week</span><b>{completionPct}%</b></div>
          </div>

          <div className="rail-card" data-testid="dashboard-poured">
            <h3><Plus />Your workspace</h3>
            <div className="pour-grid">
              <div className="pour hero"><div className="v">{cards.length}</div><div className="k">recent Action Cards</div></div>
              <div className="pour"><div className="v">{totalStat}</div><div className="k">tasks tracked</div></div>
              <div className="pour"><div className="v">{completed}</div><div className="k">tasks completed</div></div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
